import { supabase } from '../lib/supabase';

// ============================================
// ADMIN ACTIVITY LOG SERVICE
// ============================================

export const adminActivityService = {
  // Log admin activity
  async log(activityData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    const { error } = await supabase?.rpc('log_admin_activity', {
      p_admin_id: user?.id,
      p_action_type: activityData?.actionType,
      p_action_description: activityData?.actionDescription,
      p_target_entity_type: activityData?.targetEntityType || null,
      p_target_entity_id: activityData?.targetEntityId || null,
      p_changes: activityData?.changes || {},
      p_metadata: activityData?.metadata || {}
    });

    if (error) console.error('Failed to log admin activity:', error);
  },

  // Get activity log
  async getLog(filters = {}) {
    let query = supabase?.from('admin_activity_log')?.select('*')?.order('created_at', { ascending: false });

    if (filters?.adminId) {
      query = query?.eq('admin_id', filters?.adminId);
    }

    if (filters?.actionType) {
      query = query?.eq('action_type', filters?.actionType);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      adminId: obj?.admin_id,
      adminEmail: obj?.admin_email,
      actionType: obj?.action_type,
      actionDescription: obj?.action_description,
      targetEntityType: obj?.target_entity_type,
      targetEntityId: obj?.target_entity_id,
      changes: obj?.changes,
      metadata: obj?.metadata,
      createdAt: obj?.created_at
    };
  }
};

// ============================================
// ADMIN MESSAGES SERVICE
// ============================================

export const adminMessagesService = {
  // Get all messages (admin view)
  async getAll(filters = {}) {
    let query = supabase?.from('admin_messages')?.select('*, created_by_admin:user_profiles!from_admin_id(full_name, email), to_player:player_master_profiles!to_player_id(full_name), to_user:user_profiles!to_user_id(full_name, email)')?.order('created_at', { ascending: false });

    if (filters?.isActive !== undefined) {
      query = query?.eq('is_active', filters?.isActive);
    }

    if (filters?.messageType) {
      query = query?.eq('message_type', filters?.messageType);
    }

    if (filters?.targetPlayerId) {
      query = query?.eq('target_player_id', filters?.targetPlayerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Get messages for current player (player view)
  async getForCurrentPlayer() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('admin_messages')?.select('*')?.eq('to_user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Create message
  async create(messageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeCaseData = this.toSnakeCase(messageData);
    snakeCaseData.from_admin_id = user?.id;

    const { data, error } = await supabase?.from('admin_messages')?.insert([snakeCaseData])?.select()?.single();

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'message_created',
      actionDescription: `Created message: ${messageData?.title}`,
      targetEntityType: 'admin_message',
      targetEntityId: data?.id,
      metadata: { messageType: messageData?.messageType }
    });

    return { data: this.toCamelCase(data) };
  },

  // Update message
  async update(id, messageData) {
    const snakeCaseData = this.toSnakeCase(messageData);
    const { data, error } = await supabase?.from('admin_messages')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'message_updated',
      actionDescription: `Updated message: ${data?.title}`,
      targetEntityType: 'admin_message',
      targetEntityId: id,
      changes: snakeCaseData
    });

    return { data: this.toCamelCase(data) };
  },

  // Delete message
  async delete(id) {
    const { error } = await supabase?.from('admin_messages')?.delete()?.eq('id', id);

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'message_deleted',
      actionDescription: `Deleted message ID: ${id}`,
      targetEntityType: 'admin_message',
      targetEntityId: id
    });

    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      fromAdminId: obj?.from_admin_id,
      createdByAdmin: obj?.created_by_admin,
      toPlayerId: obj?.to_player_id,
      toPlayer: obj?.to_player,
      toUserId: obj?.to_user_id,
      toUser: obj?.to_user,
      isGlobal: obj?.is_global,
      messageType: obj?.message_type,
      title: obj?.title,
      description: obj?.description,
      icon: obj?.icon,
      iconColor: obj?.icon_color,
      isActive: obj?.is_active,
      priority: obj?.priority,
      displayStartDate: obj?.display_start_date,
      displayEndDate: obj?.display_end_date,
      metadata: obj?.metadata,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      target_player_id: obj?.targetPlayerId || null,
      target_user_id: obj?.targetUserId || null,
      is_global: obj?.isGlobal,
      message_type: obj?.messageType,
      title: obj?.title,
      description: obj?.description,
      icon: obj?.icon,
      icon_color: obj?.iconColor,
      is_active: obj?.isActive,
      priority: obj?.priority,
      display_start_date: obj?.displayStartDate,
      display_end_date: obj?.displayEndDate,
      metadata: obj?.metadata
    };
  }
};

// ============================================
// ADMIN CLUB INTEREST MANAGEMENT
// ============================================

export const adminClubInterestService = {
  // Get all club interests
  async getAll() {
    const { data, error } = await supabase?.from('club_interest')?.select('*, player:player_master_profiles(full_name, primary_position, current_club), club:user_profiles!club_id(full_name, organization)')?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Create club interest (admin can create on behalf)
  async create(interestData) {
    const snakeCaseData = this.toSnakeCase(interestData);
    const { data, error } = await supabase?.from('club_interest')?.insert([snakeCaseData])?.select()?.single();

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'club_interest_created',
      actionDescription: `Created club interest for player`,
      targetEntityType: 'club_interest',
      targetEntityId: data?.id,
      metadata: { playerId: interestData?.playerId, clubId: interestData?.clubId }
    });

    return { data: this.toCamelCase(data) };
  },

  // Update club interest
  async update(id, interestData) {
    const snakeCaseData = this.toSnakeCase(interestData);
    const { data, error } = await supabase?.from('club_interest')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'club_interest_updated',
      actionDescription: `Updated club interest ID: ${id}`,
      targetEntityType: 'club_interest',
      targetEntityId: id,
      changes: snakeCaseData
    });

    return { data: this.toCamelCase(data) };
  },

  // Delete club interest
  async delete(id) {
    const { error } = await supabase?.from('club_interest')?.delete()?.eq('id', id);

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'club_interest_deleted',
      actionDescription: `Deleted club interest ID: ${id}`,
      targetEntityType: 'club_interest',
      targetEntityId: id
    });

    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      player: obj?.player,
      clubId: obj?.club_id,
      club: obj?.club,
      interestLevel: obj?.interest_level,
      notes: obj?.notes,
      priority: obj?.priority,
      isActive: obj?.is_active,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId || null,
      club_id: obj?.clubId || null,
      interest_level: obj?.interestLevel,
      notes: obj?.notes,
      priority: obj?.priority,
      is_active: obj?.isActive
    };
  }
};

// ============================================
// ADMIN ANALYTICS MANAGEMENT
// ============================================

export const adminAnalyticsService = {
  // Update player analytics (admin override)
  async updatePlayerAnalytics(playerId, analyticsData) {
    const snakeCaseData = {
      total_views: analyticsData?.totalViews,
      scout_views: analyticsData?.scoutViews,
      club_views: analyticsData?.clubViews,
      agent_views: analyticsData?.agentViews,
      active_club_interest: analyticsData?.activeClubInterest,
      total_scout_feedback: analyticsData?.totalScoutFeedback,
      avg_scout_rating: analyticsData?.avgScoutRating,
      views_last_7_days: analyticsData?.viewsLast7Days,
      views_last_30_days: analyticsData?.viewsLast30Days,
      top_interested_positions: analyticsData?.topInterestedPositions,
      top_interested_leagues: analyticsData?.topInterestedLeagues,
      last_updated: new Date()?.toISOString()
    };

    const { data, error } = await supabase?.from('visitor_analytics_summary')?.upsert({ player_id: playerId, ...snakeCaseData })?.select()?.single();

    if (error) throw error;

    // Log activity
    await adminActivityService?.log({
      actionType: 'analytics_updated',
      actionDescription: `Updated analytics for player`,
      targetEntityType: 'visitor_analytics_summary',
      targetEntityId: data?.id,
      metadata: { playerId },
      changes: snakeCaseData
    });

    return { data };
  }
};

export default {
  adminMessagesService,
  adminClubInterestService,
  adminAnalyticsService,
  adminActivityService
};